import React, { createContext, useState, useEffect, useContext } from 'react';
import { Routes, Route } from 'react-router-dom';
import api from 'api';

import RequireAuth from 'helpers/require-auth';
import OktaLogin from 'containers/login/okta-widget/okta-login';
import MaintenancePage from 'containers/maintenance-page';
import AuthenticatedApp from 'containers/authenticated-app';
import EnrollAccount from 'containers/enroll/enroll-account';
import ResetPassword from 'containers/reset-password';
import RequestPassword from 'containers/request-password';
import ToastContainer from 'components/partials/toast/toast-container/toast-container';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

const DEFAULT_FEATURE_FLAGS = {
  articles: false,
  webinars: true,
  downloads: true,
  guides: true,
  courses: true,
  'tab-goals': false,
};

export const FeatureFlagsContext = createContext({
  featureFlags: DEFAULT_FEATURE_FLAGS,
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

const FeatureFlagsProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState(DEFAULT_FEATURE_FLAGS);
  const user = useCurrentUser();

  useEffect(() => {
    if (!user?.data?.oktaUserId) return;

    const fetchFeatureFlags = async () => {
      try {
        const response = await api.getFeatureFlagsSetting(user.data.oktaUserId);
        const { data } = response;

        if (data) {
          setFeatureFlags({
            ...DEFAULT_FEATURE_FLAGS,
            articles: data['cms-articles'] || DEFAULT_FEATURE_FLAGS.articles,
            'tab-goals':
              data['tab-goals'] || DEFAULT_FEATURE_FLAGS['tab-goals'],
          });
        }
      } catch (error) {
        console.error('Failed to fetch feature flags:', error);
      }
    };

    fetchFeatureFlags();
  }, [user?.data?.oktaUserId]);

  return (
    <FeatureFlagsContext.Provider value={{ featureFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

const AppRoutes = () => (
  <Routes>
    <Route path="/login/*" element={<OktaLogin />} />
    <Route path="/implicit" element={<OktaLogin />} />
    <Route path="/implicit/callback" element={<OktaLogin />} />
    <Route
      path="/account/:oktaUserId/:activationKey"
      element={<EnrollAccount />}
    />
    <Route path="/account/reset" element={<ResetPassword />} />
    <Route
      path="/account/request-password-reset"
      element={<RequestPassword />}
    />

    <Route
      path="/*"
      element={
        <RequireAuth>
          <MaintenancePage>
            <AuthenticatedApp />
          </MaintenancePage>
        </RequireAuth>
      }
    />
  </Routes>
);

function App() {
  return (
    <FeatureFlagsProvider>
      <ToastContainer />
      <AppRoutes />
      <div id="menu-portal"></div>
      <div id="page-overlay"></div>
      <div id="modal-overlay"></div>
    </FeatureFlagsProvider>
  );
}

export default App;
